import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import NoticiaImage from '../components/NoticiaImage';
import { NoticiaVideoBlock, NoticiaImagenesCarousel } from '../components/NoticiaCardMedia';
import { ArticleSchema } from '../components/SchemaMarkup';
import { getFileUrl } from '../utils/fileUtils';
import { parseNoticiaImagenes, getYoutubeEmbedUrl } from '../utils/noticiaMedia';
import './NoticiaDetalle.css';

const NoticiaDetalle = () => {
  const { id } = useParams();

  const { data: noticia, isLoading } = useQuery({
    queryKey: ['noticia', id],
    queryFn: async () => {
      const response = await api.get(`/noticias/${id}`);
      return response.data;
    }
  });

  if (isLoading) {
    return <div className="loading">Cargando noticia...</div>;
  }

  let documentosAdicionales = [];
  if (noticia?.documentos_adicionales) {
    try {
      documentosAdicionales = JSON.parse(noticia.documentos_adicionales);
    } catch (e) {}
  }

  if (!noticia) {
    return (
      <div className="not-found">
        <h2>Noticia no encontrada</h2>
        <p>Es posible que el enlace sea incorrecto.</p>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', justifyContent: 'center' }}>
          <Link to="/noticias" className="btn">Volver a noticias</Link>
          <Link to="/convocatorias" className="btn">Ver convocatorias</Link>
        </div>
      </div>
    );
  }

  const urlsImagenes = parseNoticiaImagenes(noticia);
  const embedUrl = getYoutubeEmbedUrl(noticia.video_url);
  const facebookUrl = (noticia.facebook_url || '').trim();
  const facebookEmbedSrc = facebookUrl
    ? `https://www.facebook.com/plugins/post.php?href=${encodeURIComponent(facebookUrl)}&show_text=true&width=500`
    : null;
  const schemaImage = urlsImagenes[0] ? getFileUrl(urlsImagenes[0]) : undefined;

  return (
    <div className="noticia-detalle">
      <ArticleSchema
        headline={noticia.titulo}
        description={noticia.resumen || noticia.contenido.substring(0, 200)}
        image={schemaImage}
        datePublished={noticia.fecha_publicacion || noticia.creado_en}
        dateModified={noticia.actualizado_en || noticia.fecha_actualizacion || noticia.fecha_publicacion || noticia.creado_en}
      />
      <article className="noticia-detalle-article">
        <div className="noticia-detalle-container">
          <Link to="/noticias" className="back-link">← Volver a noticias</Link>

          <header className="noticia-header">
            <div className="noticia-fechas">
              <span className="noticia-fecha">
                Publicado: {new Date(noticia.fecha_publicacion || noticia.creado_en).toLocaleDateString('es-CO', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </span>
              {(noticia.actualizado_en || noticia.fecha_actualizacion) && 
               new Date(noticia.actualizado_en || noticia.fecha_actualizacion).getTime() !== 
               new Date(noticia.fecha_publicacion || noticia.creado_en).getTime() && (
                <span className="noticia-actualizacion">
                  Última actualización: {new Date(noticia.actualizado_en || noticia.fecha_actualizacion).toLocaleDateString('es-CO', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              )}
            </div>
            <h1>{noticia.titulo}</h1>
            {noticia.categoria && (
              <span className="noticia-categoria">{noticia.categoria}</span>
            )}
            {facebookUrl && noticia.categoria !== 'Facebook' && (
              <span className="noticia-categoria noticia-categoria-facebook">Facebook</span>
            )}
          </header>

          <div className="noticia-body-wrap">
            {urlsImagenes.length > 1 && (
              <div className="noticia-detalle-carrusel-outer">
                <NoticiaImagenesCarousel
                  urls={urlsImagenes}
                  alt={noticia.titulo}
                  wrapperClassName="noticia-detalle-carousel-img"
                />
              </div>
            )}
            {urlsImagenes.length === 1 && (
              <figure className="noticia-detail-figure">
                <NoticiaImage
                  src={urlsImagenes[0]}
                  alt={noticia.titulo}
                  className="noticia-detail-image"
                />
              </figure>
            )}
            {embedUrl && (
              <div className="noticia-video-embed">
                <NoticiaVideoBlock embedUrl={embedUrl} />
              </div>
            )}
            {facebookEmbedSrc && (
              <div className="noticia-facebook-embed">
                <iframe
                  title="Publicación en Facebook"
                  src={facebookEmbedSrc}
                  className="noticia-facebook-iframe"
                  scrolling="no"
                  frameBorder="0"
                  allow="encrypted-media; fullscreen"
                  allowFullScreen
                />
                <a
                  href={facebookUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn noticia-facebook-link-btn"
                >
                  Ver publicación original en Facebook
                </a>
              </div>
            )}
            <div className="noticia-body">
              <div dangerouslySetInnerHTML={{ __html: noticia.contenido }} />
            </div>
            {documentosAdicionales.length > 0 && (
              <div className="noticia-documentos">
                <h3>Documentos adjuntos</h3>
                <ul>
                  {documentosAdicionales.map((doc, idx) => (
                    <li key={idx}>
                      <a href={getFileUrl(doc.ruta)} target="_blank" rel="noopener noreferrer" download>
                        {doc.nombre}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </article>
    </div>
  );
};

export default NoticiaDetalle;


